<template>
  <div>
    <div class="overlay" data-test-header-menu-overlay @click="$emit('overlay-click')" />
    <nav id="header-menu-nav" ref="header-menu-nav" class="header-menu">
      <text-field
        class="header-menu__textfield"
        :placeholder="fieldPlaceholder"
        :value="inputFieldValue"
        @input="$emit('input', $event)"
        @enter="$emit('search')"
      >
        <icon
          data-test-header-menu-search-icon
          class="header-menu__textfield-icon"
          icon="search"
          @click="$emit('search')"
        />
      </text-field>
      <cvi-header-menu-item
        v-for="(menuLink, key) in menuLinks"
        :key="key"
        data-test-header-public-link
        :url="getLinkUrl(menuLink.link)"
        :logged-in-url="getLinkUrl(menuLink.loggedInLink)"
        :is-logged-in="isMemberLoggedIn"
        @click="onMenuItemClick(menuLink.label)"
      >
        {{ decode(menuLink.label) }}
      </cvi-header-menu-item>
      <div class="header-menu__line-break" />
      <cvi-header-menu-item
        v-if="minSideLink"
        id="header-min-side-menu-item"
        data-test-min-side-link
        :url="minSideLink.url"
      >
        <div class="header-menu__min-side-label">
          <icon-button icon="lock" :small="true" :alt-color="true" aria-labelledby="header-minside-button" tabindex="-1" />
          <span id="header-minside-button">{{ decode(minSideLabel) }}</span>
        </div>
      </cvi-header-menu-item>
      <div v-if="isMemberLoggedIn">
        <cvi-header-menu-item
          v-for="(menuLinkItem, key) in loggedInLinks"
          :key="key"
          data-test-header-logged-in-link
          :url="createLoggedInLink(menuLinkItem)"
        >
          {{ decode(menuLinkItem.label) }}
        </cvi-header-menu-item>
      </div>

      <div v-if="showLogoutButton" class="header-menu__button-container">
        <cvi-header-menu-button
          data-test-header-logout-button
          :label="logoutLabel"
          @click="logoutButtonClick"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            display="block"
          >
            <path
              fill="inherit"
              d="M15.325 16.275a1.112 1.112 0 0 1-.275-.738c0-.275.092-.504.275-.687l1.85-1.85H10a.965.965 0 0 1-.712-.288A.965.965 0 0 1 9 12c0-.283.096-.521.288-.713A.967.967 0 0 1 10 11h7.175l-1.85-1.85c-.2-.2-.3-.437-.3-.712 0-.275.1-.513.3-.713.183-.2.413-.3.688-.3.275 0 .504.092.687.275l3.6 3.6c.1.1.171.208.213.325.041.117.062.242.062.375s-.02.258-.062.375a.883.883 0 0 1-.213.325l-3.6 3.6c-.217.217-.454.312-.712.287a1.052 1.052 0 0 1-.663-.312ZM5 21c-.55 0-1.021-.196-1.413-.587A1.928 1.928 0 0 1 3 19V5c0-.55.196-1.021.587-1.413A1.928 1.928 0 0 1 5 3h6a.97.97 0 0 1 .713.287A.97.97 0 0 1 12 4c0 .283-.096.52-.287.712A.968.968 0 0 1 11 5H5v14h6c.283 0 .521.096.713.288A.967.967 0 0 1 12 20c0 .283-.096.52-.287.712A.968.968 0 0 1 11 21H5Z"
            />
          </svg>
        </cvi-header-menu-button>
      </div>
    </nav>
  </div>
</template>

<script lang="ts">
import CviHeaderMenuItem from './CviHeaderMenuItem.vue';
import CviHeaderMenuButton from './CviHeaderMenuButton.vue';
import TextField from './TextField.vue';
import Icon from './Icon.vue';
import type { LoggedInMenuLink, MenuLink } from './types/CviHeader';
import IconButton from './IconButton.vue';
import type { Link } from '@/interfaces/Link';
import { defaultLink } from '@/interfaces/Link';
import Utility from '@/common/UtilityFunctions.js';
import type { PropType } from 'vue';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'CviHeaderMenu',
  components: {
    CviHeaderMenuItem,
    TextField,
    Icon,
    IconButton,
    CviHeaderMenuButton
  },
  props: {
    menuLinks: {
      type: Array as PropType<MenuLink[]>,
      default: () => []
    },
    minSideLabel: {
      type: String,
      default: () => ''
    },
    minSideLink: {
      type: Object as PropType<Link>,
      default: () => defaultLink
    },
    loggedInLinks: {
      type: Array as PropType<LoggedInMenuLink[]>,
      default: () => []
    },
    isMemberLoggedIn: {
      type: Boolean,
      default: () => false
    },
    fieldPlaceholder: {
      type: String,
      default: () => ''
    },
    inputFieldValue: {
      type: String,
      default: () => ''
    },
    logoutLabel: {
      type: String,
      default: () => ''
    },
    logoutLink: {
      type: Object as PropType<Link>,
      default: () => defaultLink
    }
  },
  computed: {
    showLogoutButton(): boolean {
      return this.isMemberLoggedIn && Boolean(this.logoutLink.url);
    }
  },
  methods: {
    decode(label: string): string {
      return Utility.decodeHtml(label);
    },
    createLoggedInLink(loggedInLink: LoggedInMenuLink) {
      let link = '';
      if (loggedInLink.link) link = `${loggedInLink.link.url}${loggedInLink.anchor}`;

      return link;
    },
    logoutButtonClick(): void {
      window.location.href = this.logoutLink.url;
    },
    getLinkUrl(link: Link) {
      return link ? link.url : '';
    },
    onMenuItemClick(label: string): void {
      this.$store.dispatch('Events/register', {
        event: 'cvi-header-click',
        id: 'cviHeader',
        eventLabel: label
      });
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';
@import 'node_modules/foundation-sites/scss/util/util';

.header-menu {
  display: flex;
  flex-direction: column;
  background-color: rgb(255, 255, 255);
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1020;
  overflow-y: auto;
  overscroll-behavior: contain;

  a {
    display: flex;
    justify-content: space-between;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    font-size: rem-calc(16px);
    line-height: rem-calc(24px);
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    color: rgb(34, 37, 39);

    &:hover {
      color: rgb(34, 37, 39);
      background-color: rgba(58, 88, 109, 0.04);
    }
  }

  &__textfield {
    @include breakpoint(small only) {
      margin: rem-calc(16px) rem-calc(24px);
    }

    @include breakpoint(medium only) {
      margin: rem-calc(16px) rem-calc(56px);
    }

    @include breakpoint(large up) {
      display: none;
    }
  }

  &__textfield-icon {
    cursor: pointer;
  }

  &__line-break {
    margin: rem-calc(16px) rem-calc(56px);
    border-bottom: rem-calc(1px) solid rgba(204, 204, 204, 1);
    height: 1px;
  }

  &__min-side-label {
    gap: rem-calc(8px);
    padding: rem-calc(6px) 0;
    display: flex;
    align-items: center;
  }

  &__button-container {
    display: flex;
  }
}

#header-min-side-menu-item {
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.overlay {
  position: fixed;
  overscroll-behavior: contain;
  inset: 0;
  background-color: rgba(34, 37, 39, 0.24);
  z-index: 1000;
}

@include breakpoint(medium up) {
  .overlay,
  .header-menu {
    top: rem-calc(124px);
  }

  .header-menu {
    width: rem-calc(454px);
    padding-top: rem-calc(16px);
    padding-bottom: rem-calc(16px);

    a {
      padding: rem-calc(12px) rem-calc(56px);
    }

    &__button-container {
      align-self: flex-end;
      padding: rem-calc(16px) rem-calc(56px);
    }
  }
}

@include breakpoint(small only) {
  .overlay,
  .header-menu {
    top: rem-calc(80px);
  }

  .header-menu {
    width: 100%;

    a {
      padding: rem-calc(12px) rem-calc(24px);
    }

    &__line-break {
      margin: rem-calc(12px) rem-calc(24px);
    }

    &__button-container {
      padding: rem-calc(16px) rem-calc(24px);
    }
  }
}
</style>
